/**
 * WDIO Service: Runtime hooks for capturing device logs, commands,
 * assertions, screenshots, and network activity during test execution.
 */

import type { AppiumReporterConfig, CollectedTestData, DetectedCapabilities } from './types.js';
import { resolveConfig } from './config.js';
import { extractCapabilities } from './capability-detector.js';
import { DeviceLogCollector } from './device-log-collector.js';
import { ConsoleLogCollector } from './console-log-collector.js';
import { NetworkInterceptor } from './network-interceptor.js';
import { CommandTracker } from './command-tracker.js';
import { AssertionTracker } from './assertion-tracker.js';
import { ScreenshotCollector } from './screenshot-collector.js';
import type { AssertionHookParams, AfterAssertionHookParams } from './assertion-tracker.js';
import { registerService } from './service-registry.js';

type DriverLike = {
  getLogs(type: string): Promise<Array<{ level: string; timestamp: number; message: string }>>;
  getLogTypes(): Promise<string[]>;
  saveScreenshot(filepath: string): Promise<void>;
  execute(script: string, args?: unknown[]): Promise<unknown>;
  sessionSubscribe?(params: { events: string[] }): Promise<void>;
  on?(event: string, handler: (...args: unknown[]) => void): void;
  capabilities?: Record<string, unknown>;
  requestedCapabilities?: Record<string, unknown>;
};

const INTERNAL_COMMANDS = new Set([
  'getLogTypes', 'getLogs', 'saveScreenshot', 'getTitle', 'getUrl',
  'status', 'getSession', 'deleteSession', 'createSession',
  'getWindowHandle', 'getWindowHandles', 'getTimeouts',
]);

export class TestRelicService {
  private config: ReturnType<typeof resolveConfig>;
  private detectedCaps: DetectedCapabilities | null = null;
  private driver: DriverLike | null = null;

  private deviceLogCollector: DeviceLogCollector | null = null;
  private consoleLogCollector: ConsoleLogCollector | null = null;
  private networkInterceptor: NetworkInterceptor | null = null;
  private commandTracker: CommandTracker;
  private assertionTracker: AssertionTracker;
  private screenshotCollector: ScreenshotCollector | null = null;

  /** Offsets into the collectors at the start of each test (for per-test slicing). */
  private testStartDeviceLogOffset = 0;
  private testStartNetworkOffset = 0;

  constructor(options?: Partial<AppiumReporterConfig>) {
    this.config = resolveConfig(options);
    this.commandTracker = new CommandTracker(this.config.includeCommands);
    this.assertionTracker = new AssertionTracker(this.config.includeAssertions);
  }

  async before(
    capabilities: Record<string, unknown>,
    _specs: string[],
    browser: DriverLike,
  ): Promise<void> {
    this.driver = browser;

    const caps = {
      ...(browser.capabilities ?? {}),
      ...(browser.requestedCapabilities ?? {}),
      ...capabilities,
    };
    this.detectedCaps = extractCapabilities(caps);

    this.deviceLogCollector = new DeviceLogCollector({
      pollInterval: this.config.deviceLogPollInterval,
      platform: this.detectedCaps.platform,
      enabled: this.config.includeDeviceLogs,
    });
    await this.deviceLogCollector.start(browser);

    this.consoleLogCollector = new ConsoleLogCollector({
      enabled: this.config.includeConsoleLogs,
      preferBiDi: this.config.preferBiDi,
      platform: this.detectedCaps.platform,
      pollInterval: this.config.deviceLogPollInterval,
    });
    await this.consoleLogCollector.start(browser);

    const deviceSerial =
      this.detectedCaps.udid ?? this.detectedCaps.deviceName ?? undefined;

    this.networkInterceptor = new NetworkInterceptor({
      enabled: this.config.includeNetworkLogs,
      platform: this.detectedCaps.platform,
      pollInterval: this.config.deviceLogPollInterval,
      deviceSerial,
    });
    await this.networkInterceptor.start(browser);

    const outputDir = this.config.outputPath.replace(/[^/\\]+$/, 'artifacts');
    this.screenshotCollector = new ScreenshotCollector({
      enabled: this.config.includeScreenshots,
      screenshotOnEvery: this.config.screenshotOnEvery,
      includeVideoRecording: this.config.includeVideoRecording,
      outputDir,
      deviceSerial,
    });

    registerService(this);
  }

  async after(): Promise<void> {
    this.deviceLogCollector?.stop();
    this.consoleLogCollector?.stop();
    this.networkInterceptor?.stop();
  }

  async beforeTest(): Promise<void> {
    this.commandTracker.clear();
    this.assertionTracker.clear();
    this.screenshotCollector?.clear();
    // Snapshot log offsets so getCollectedData() returns only this test's logs
    this.testStartDeviceLogOffset = this.deviceLogCollector?.getCount() ?? 0;
    this.testStartNetworkOffset = this.networkInterceptor?.getCount() ?? 0;
    this.networkInterceptor?.snapshotProxyOffset();

    // Start video recording fresh for each test (so each test gets its own clip)
    if (this.config.includeVideoRecording && this.driver && this.screenshotCollector) {
      await this.screenshotCollector.startVideoRecording(this.driver);
    }
  }

  async afterTest(
    test: { title?: string; fullTitle?: string } | unknown,
    _context: unknown,
    result: { passed: boolean },
  ): Promise<void> {
    // Stop video recording and save the clip for this test
    if (this.config.includeVideoRecording && this.driver && this.screenshotCollector) {
      const testTitle = (test as { title?: string })?.title ?? 'test';
      await this.screenshotCollector.stopVideoRecording(this.driver, testTitle);
    }

    if (!result.passed && this.driver && this.screenshotCollector) {
      await this.screenshotCollector.captureOnFailure(this.driver, 'test-failure');
    }

    if (this.config.screenshotOnEvery === 'test' && this.driver && this.screenshotCollector) {
      await this.screenshotCollector.captureScreenshot(this.driver, 'test-end');
    }
  }

  beforeCommand(commandName: string, args: unknown[]): void {
    if (INTERNAL_COMMANDS.has(commandName)) return;
    this.commandTracker.beforeCommand(commandName, args);
  }

  afterCommand(commandName: string, args: unknown[], result: unknown, error?: Error): void {
    if (INTERNAL_COMMANDS.has(commandName)) return;
    this.commandTracker.afterCommand(commandName, args, result, error);
  }

  beforeAssertion(params: AssertionHookParams): void {
    this.assertionTracker.beforeAssertion(params);
  }

  afterAssertion(params: AfterAssertionHookParams): void {
    this.assertionTracker.afterAssertion(params);
  }

  getCollectedData(): CollectedTestData {
    return {
      commands: this.commandTracker.getSteps(),
      assertions: this.assertionTracker.getSteps(),
      deviceLogs: this.deviceLogCollector?.getEntriesSince(this.testStartDeviceLogOffset) ?? [],
      networkEntries: this.networkInterceptor?.getEntriesSince(this.testStartNetworkOffset) ?? [],
      screenshotPaths: this.screenshotCollector?.getScreenshotPaths() ?? [],
      videoPath: this.screenshotCollector?.getVideoPath() ?? null,
      detectedCaps: this.detectedCaps ?? {
        platform: 'unknown',
        testMode: 'native',
        platformVersion: null,
        deviceName: null,
        automationName: null,
        appPath: null,
        browserName: null,
        udid: null,
      },
    };
  }

  dispose(): void {
    this.deviceLogCollector?.dispose();
    this.consoleLogCollector?.dispose();
    this.networkInterceptor?.dispose();
    this.commandTracker.dispose();
    this.assertionTracker.dispose();
    this.screenshotCollector?.dispose();
  }
}

export default TestRelicService;
